import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { AmbientParticles } from "./AmbientParticles";

const HERO_IMG =
  "https://images.unsplash.com/photo-1693578538512-fc66f318c833?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzcGElMjBpbnRlcmlvcnxlbnwwfHx8fDE3OTIwOTU1OTF8MA&ixlib=rb-4.1.0&q=70&w=1600&auto=format";

const LINES = [
  { text: "Every lead answered", cls: "text-pearl" },
  { text: "in under 5 minutes.", cls: "italic text-champagne" },
  { text: "Every consult worked", cls: "text-pearl" },
  { text: "until it books.", cls: "italic text-champagne" },
];

const TRUST = ["HIPAA-aware", "Founder-led", "Built inside a working Houston med spa"];

const ResponseCounter = () => {
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { stiffness: 18, damping: 14 });
  const display = useTransform(spring, (v) => {
    const s = Math.round(v);
    return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  });
  useEffect(() => {
    const t = setTimeout(() => mv.set(252), 2200);
    return () => clearTimeout(t);
  }, [mv]);
  return <motion.span data-testid="response-counter">{display}</motion.span>;
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover" fetchpriority="high" />
        <div className="absolute inset-0 bg-white/65" />
        <div className="absolute inset-0 hero-vignette" />
      </motion.div>

      <motion.div
        aria-hidden="true"
        animate={{ x: [0, 70, -40, 0], y: [0, -50, 30, 0], opacity: [0.5, 0.9, 0.6, 0.5] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -z-10 top-1/4 left-1/4 h-[480px] w-[480px] rounded-full bg-champagne/[0.12] blur-[140px]"
      />
      <AmbientParticles />

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-mono text-xs tracking-[0.4em] uppercase text-champagne mb-10"
          data-testid="hero-eyebrow"
        >
          For aesthetic, surgical, post-op and wellness practice owners
        </motion.p>

        <h1 className="font-display font-light tracking-tight text-[2.6rem] sm:text-6xl lg:text-[5.2rem] leading-[1.04]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 0.35 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${line.cls}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base md:text-lg text-zinc-600 font-light leading-relaxed"
        >
          North &amp; Noble installs a fully managed front desk, pipeline and booking team for med
          spas, so you stop losing patients you already paid to generate.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href="#book"
            data-testid="hero-request-call-cta"
            className="btn-shine inline-flex items-center gap-3 rounded-full bg-champagne px-8 py-4 text-sm font-medium tracking-wide text-white hover:bg-champagne-soft transition-colors duration-300 group shadow-[0_10px_40px_rgba(61,165,221,0.35)]"
          >
            Book My Revenue Leak Audit
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </a>
          <a
            href="#calculator"
            data-testid="hero-see-leak-cta"
            className="inline-flex items-center gap-3 rounded-full border border-black/15 bg-white/70 px-8 py-4 text-sm tracking-wide text-zinc-700 hover:border-champagne/60 hover:text-champagne transition-colors duration-300 group"
          >
            See what I'm losing
            <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.7 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4"
        >
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-champagne animate-pulse-gold" />
            <span className="text-sm text-zinc-600">
              Average first response:{" "}
              <span className="font-mono text-base text-champagne">
                <ResponseCounter />
              </span>
            </span>
          </div>
          <div className="hidden sm:block h-4 w-px bg-black/15" />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="hero-trust-bar">
            {TRUST.map((t, i) => (
              <span key={t} className="flex items-center gap-3 text-xs font-mono tracking-widest uppercase text-zinc-500">
                {i > 0 && <span className="text-champagne">|</span>}
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 1.9, ease: [0.16, 1, 0.3, 1] }}
        className="hidden lg:block absolute right-16 top-1/3 [perspective:1200px]"
        data-testid="hero-live-card"
      >
        <motion.div animate={{ y: [0, -14, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
          <motion.div
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="w-72 border border-black/10 bg-white/80 backdrop-blur-xl p-6 shadow-[0_24px_60px_rgba(14,20,27,0.15)]"
          >
            <div className="flex items-center gap-2 mb-5">
              <span className="h-2 w-2 rounded-full bg-champagne animate-pulse-gold" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500">Live intake</span>
            </div>
            <div className="space-y-4 font-mono text-xs">
              <div>
                <p className="text-zinc-500">02:14 AM</p>
                <p className="text-pearl mt-0.5">Web inquiry received</p>
              </div>
              <div>
                <p className="text-champagne">04m 12s</p>
                <p className="text-pearl mt-0.5">First response sent</p>
              </div>
              <div className="border-t border-black/10 pt-4">
                <p className="text-zinc-500">Thu · 2:00 PM</p>
                <p className="text-pearl mt-0.5">Consult booked. Deposit collected</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.a
        href="#speed-demo"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{
          opacity: { duration: 1, delay: 2.4 },
          y: { duration: 2, repeat: Infinity, ease: "easeInOut", delay: 2.4 },
        }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-500 hover:text-champagne transition-colors duration-300"
      >
        <span className="font-mono text-[10px] tracking-[0.35em] uppercase">Scroll</span>
        <ArrowDown size={14} />
      </motion.a>
    </section>
  );
};
